import { Ban, CheckCircle2, Circle, Hourglass, MessageSquare, X } from 'lucide-react';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import type { TrailerModel } from '@/service/types/trailerTypes';
import type { ReactNode } from 'react';

interface TrailerInformationPopupProps {
  trailer: TrailerModel;
  isOpen: boolean;
  onClose: () => void;
}

// ── Status icon — Matches Xamarin TrailerStatusToFilterImageConverter ──
// Colors from YMSColorConstant

function ConditionIcon({ status }: { status: string }) {
  switch (status) {
    case 'Damaged':
      return <Ban className="h-[15px] w-[15px] text-[#FC3B3B] shrink-0" aria-hidden="true" />;
    case 'Loaded':
    case 'IB Loaded':
    case 'OB Loaded':
      return (
        <CheckCircle2 className="h-[15px] w-[15px] text-[#00D649] shrink-0" aria-hidden="true" />
      );
    case 'IB Loading':
    case 'OB Loading':
    case 'IB Unloading':
      return <Hourglass className="h-[15px] w-[15px] text-[#EDBE18] shrink-0" aria-hidden="true" />;
    case 'Empty':
      return <Circle className="h-[15px] w-[15px] text-[#333333] shrink-0" aria-hidden="true" />;
    default:
      return <Circle className="h-[15px] w-[15px] text-[#333333] shrink-0" aria-hidden="true" />;
  }
}

// Matches Xamarin TrailerStatusTextColorConverter
function getConditionColor(status: string): string {
  switch (status) {
    case 'Damaged':
      return '#FC3B3B';
    case 'Loaded':
    case 'IB Loaded':
    case 'OB Loaded':
      return '#00D649';
    case 'IB Loading':
    case 'OB Loading':
    case 'IB Unloading':
      return '#EDBE18';
    case 'Empty':
      return '#333333';
    default:
      return '#333333';
  }
}

// ── Field pair — Matches Xamarin Grid (2 cols, RowSpacing=15) ──
// Labels: TrailerTitleLabelStyle (#8A8A8A, PoppinsSemiBold)
// Values: TrailerValueLabelStyle (#8A8A8A, PoppinsRegular)

interface FieldPairProps {
  leftLabel: string;
  leftValue: string | number | undefined | null;
  leftPrefix?: ReactNode | undefined;
  rightLabel?: string | undefined;
  rightValue?: string | number | undefined | null;
  rightPrefix?: ReactNode | undefined;
  rightValueColor?: string | undefined;
}

function FieldPair({
  leftLabel,
  leftValue,
  leftPrefix,
  rightLabel,
  rightValue,
  rightPrefix,
  rightValueColor,
}: FieldPairProps) {
  // Matches Xamarin DataTrigger: show "-" when value IsNullOrEmpty
  const displayLeft =
    leftValue === null || leftValue === undefined || leftValue === '' ? '-' : String(leftValue);
  const displayRight =
    rightValue === null || rightValue === undefined || rightValue === '' ? '-' : String(rightValue);

  return (
    <div className="grid grid-cols-1 xs:grid-cols-2 gap-x-4 gap-y-2 xs:gap-y-0 py-[7px]">
      {/* Left column */}
      <div className="min-w-0">
        <p className="text-xs sm:text-sm md:text-lg font-poppins font-semibold text-[#8A8A8A] mb-1">
          {leftLabel}
        </p>
        <div className="flex items-center gap-2">
          {leftPrefix}
          <p className="text-[10px] sm:text-xs md:text-sm font-poppins text-[#8A8A8A] break-words">
            {displayLeft}
          </p>
        </div>
      </div>

      {/* Right column */}
      {rightLabel !== undefined && (
        <div className="min-w-0">
          <p className="text-xs sm:text-sm md:text-lg font-poppins font-semibold text-[#8A8A8A] mb-1">
            {rightLabel}
          </p>
          <div className="flex items-center gap-2">
            {rightPrefix}
            <p
              className="text-[10px] sm:text-xs md:text-sm font-poppins break-words"
              style={{ color: rightValueColor ?? '#8A8A8A' }}
            >
              {displayRight}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export function TrailerInformationPopup({
  trailer,
  isOpen,
  onClose,
}: TrailerInformationPopupProps) {
  const { t } = useTranslation();
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    },
    [onClose]
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
        document.body.style.overflow = '';
      };
    }
    return undefined;
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  // Status/condition display
  const conditionDisplay = trailer.status;
  const conditionColor = getConditionColor(conditionDisplay);
  const isDamaged = conditionDisplay === 'Damaged';

  // Formatted values matching Xamarin FormattedString patterns
  const elapsedDisplay =
    trailer.elapsedMinutes !== undefined
      ? `${String(trailer.elapsedMinutes)} ${t('trailerInfo.minutes')}`
      : undefined;

  const dwellingDisplay = trailer.dwellTime
    ? `${trailer.dwellTime} ${t('trailerInfo.hours')}`
    : undefined;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-2 sm:p-4 animate-in fade-in duration-200"
      onClick={onClose}
      role="presentation"
    >
      {/* Popup Frame — Matches Xamarin: Width=700(d)/400(p), Height=600, CornerRadius=22 */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */}
      <div
        className="w-[calc(100vw-16px)] xs:w-[360px] sm:w-[400px] md:w-[700px] max-h-[calc(100%-16px)] sm:max-h-[calc(100%-32px)] bg-white rounded-[16px] xs:rounded-[22px] shadow-xl flex flex-col animate-in zoom-in-95 duration-200"
        onClick={(e) => {
          e.stopPropagation();
        }}
        role="dialog"
        aria-modal="true"
        aria-label={t('trailerInfo.title')}
      >
        {/* Row 0: Close icon — Matches Xamarin: CloseIconDark SVG, 25x25, EndAndExpand */}
        <div className="flex justify-end px-5 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
            aria-label={t('common.close')}
          >
            <X className="h-[25px] w-[25px] text-[#445E71]" />
          </button>
        </div>

        {/* Row 1: ScrollView content */}
        <div
          className="flex-1 overflow-y-auto min-h-0 px-3 xs:px-5 pb-5 md:px-8"
          style={{ scrollbarWidth: 'thin', scrollbarColor: '#D2DAE1 transparent' }}
        >
          {/* ── Section 1: Trailer & Location Details ── */}
          <h2 className="text-sm sm:text-base md:text-lg font-poppins font-bold text-[#243743] mb-2">
            {t('trailerInfo.title')}
          </h2>

          <div className="ml-[10px] mt-[12px] space-y-0">
            {/* Row 0: Trailer Number | Trailer Status / Condition */}
            <FieldPair
              leftLabel={t('trailerInfo.trailerNumber')}
              leftValue={trailer.trailerNumber}
              rightLabel={isDamaged ? t('trailerInfo.condition') : t('trailerInfo.trailerStatus')}
              rightValue={conditionDisplay}
              rightPrefix={<ConditionIcon status={conditionDisplay} />}
              rightValueColor={conditionColor}
            />

            {/* Row 1: Conditional — Condition (not damaged) or Damage Reason (damaged) | Location */}
            <FieldPair
              leftLabel={isDamaged ? t('trailerInfo.damageReason') : t('trailerInfo.condition')}
              leftValue={isDamaged ? (trailer.damageReason ?? '-') : (trailer.condition ?? '-')}
              rightLabel={t('trailerInfo.location')}
              rightValue={trailer.location}
            />

            {/* Row 2: Elapsed Time in Minutes | Check In Time */}
            <FieldPair
              leftLabel={t('trailerInfo.elapsedTime')}
              leftValue={elapsedDisplay}
              rightLabel={t('trailerInfo.checkInTime')}
              rightValue={trailer.checkInTime}
            />

            {/* Row 3: Dwelling Time | Scheduled Pickup */}
            <FieldPair
              leftLabel={t('trailerInfo.dwellingTime')}
              leftValue={dwellingDisplay}
              rightLabel={t('trailerInfo.scheduledPickup')}
              rightValue={trailer.scheduledPickup}
            />
          </div>

          {/* Trailer Comments — Matches Xamarin: TrailerComments icon + label */}
          <div className="ml-[10px] mt-[5px]">
            <div className="flex items-center gap-2 mb-1">
              <MessageSquare className="h-[25px] w-[25px] text-[#8A8A8A]" aria-hidden="true" />
              <p className="text-xs sm:text-sm md:text-lg font-poppins font-semibold text-[#8A8A8A]">
                {t('trailerInfo.trailerComments')}
              </p>
            </div>
            <p className="text-[10px] sm:text-xs md:text-sm font-poppins text-[#8A8A8A]">
              {trailer.comments ?? t('trailerInfo.noComments')}
            </p>
          </div>

          {/* Divider — Matches Xamarin: HeightRequest=1, bg=TrailerListFrameColor (#D0D0D0) */}
          <div className="h-px bg-[#D0D0D0] mt-4 mr-[10px]" />

          {/* ── Section 2: Customer Details ── */}
          <h2 className="text-sm sm:text-base md:text-lg font-poppins font-bold text-[#243743] mt-3 mb-2">
            {t('trailerInfo.customerDetails')}
          </h2>

          <div className="ml-[10px] mt-[12px] space-y-0">
            <FieldPair
              leftLabel={t('trailerInfo.carrier')}
              leftValue={trailer.carrierName}
              rightLabel={t('trailerInfo.customerName')}
              rightValue={trailer.customerName}
            />
            <FieldPair
              leftLabel={t('trailerInfo.driverName')}
              leftValue={trailer.driverName}
              rightLabel={t('trailerInfo.driverCell')}
              rightValue={trailer.driverCell}
            />
          </div>

          {/* Divider */}
          <div className="h-px bg-[#D0D0D0] mt-4 mr-[10px]" />

          {/* ── Section 3: Shipment Details ── */}
          <h2 className="text-sm sm:text-base md:text-lg font-poppins font-bold text-[#243743] mt-3 mb-2">
            {t('trailerInfo.shipmentDetails')}
          </h2>

          <div className="ml-[10px] mt-[12px] space-y-0">
            <FieldPair
              leftLabel={t('trailerInfo.shipmentNumber')}
              leftValue={trailer.shipmentNumber}
              rightLabel={t('trailerInfo.items')}
              rightValue={trailer.items}
            />
            <FieldPair
              leftLabel={t('trailerInfo.palletsLoaded')}
              leftValue={trailer.palletsLoaded}
              rightLabel={t('trailerInfo.po')}
              rightValue={trailer.po}
            />
            <FieldPair leftLabel={t('trailerInfo.do')} leftValue={trailer.doNumber} />
          </div>
        </div>
      </div>
    </div>
  );
}
